// ###### IMPORTS
import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import {
  PencilSquareIcon,
  TrashIcon,
  BuildingLibraryIcon,
  TagIcon,
  EllipsisVerticalIcon,
  PlusIcon,
} from '@heroicons/react/24/solid';
import { useAssetsData } from '../moduleServices/assets_data_context';
import CardAssetsImport from './viewComponents/Manage/CardAssetsImport';
import CardCustomUpdateBalances from './viewComponents/Manage/CardCustomUpdateBalances';
import CardAssetsUpdateData from './viewComponents/Manage/CardAssetsUpdateData';
import CardYearReport from './viewComponents/Manage/CardYearReport';
import ModalEditAssetLocation from './viewComponents/Manage/ModalEditAssetLocation';
import ModalEditAssetCategory from './viewComponents/Manage/ModalEditAssetCategory';
import ModalConfirm from '../moduleComponents/ModalConfirm';
import FabAssetsMenu from './viewComponents/Common/FabAssetsMenu';
import { deleteAssetLocation, deleteAssetCategory } from './viewServices/assets_api';

// ###### SUBCOMPONENTS

const EmptyState = ({ message }) => (
  <div className="flex items-center justify-center py-6 text-gray-400 text-sm">
    {message}
  </div>
);

const LoadingChipsSkeleton = () => (
  <div className="p-3 flex flex-wrap gap-2 rounded-b-xl">
    {Array.from({ length: 4 }).map((_, i) => (
      <div
        key={i}
        className="animate-pulse h-[3.25rem] w-36 rounded-xl bg-gray-100"
      />
    ))}
  </div>
);

const formatApiError = (data) => {
  if (!data) return 'Request failed.';
  if (typeof data === 'string') return data;
  if (data.detail) {
    const d = data.detail;
    return Array.isArray(d) ? d.join(' ') : String(d);
  }
  const parts = [];
  Object.keys(data).forEach((k) => {
    const v = data[k];
    if (Array.isArray(v)) parts.push(...v.map((x) => String(x)));
    else if (v != null) parts.push(String(v));
  });
  return parts.length ? parts.join(' ') : 'Request failed.';
};

/** Locations: chip layout + overflow menu (edit / delete catalog entries only). */
const LocationsChipsCard = ({ items, labelKey, subKey, loading, reload }) => {
  const [openMenuId, setOpenMenuId] = useState(null);
  const [editingLocation, setEditingLocation] = useState(null);
  const [addingLocation, setAddingLocation] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const closeLocationForm = () => {
    setAddingLocation(false);
    setEditingLocation(null);
  };

  const locationFormOpen = addingLocation || editingLocation != null;

  useEffect(() => {
    if (openMenuId == null) return;
    const handlePointerDown = (e) => {
      const root = e.target.closest('[data-location-menu-root]');
      if (root && root.getAttribute('data-location-menu-root') === String(openMenuId)) {
        return;
      }
      setOpenMenuId(null);
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [openMenuId]);

  const handleConfirmDelete = async () => {
    if (!deleteTarget?.id) return;
    try {
      setDeleting(true);
      await deleteAssetLocation(deleteTarget.id);
      toast.success('Location removed from catalog.');
      setDeleteTarget(null);
      if (reload) await reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data) || err.message || 'Failed to delete location.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <div className="bg-white shadow rounded-xl">
        <div className="rounded-t-xl overflow-hidden">
          <div className="bg-green-800 px-3 py-1.5 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <BuildingLibraryIcon className="h-4 w-4 text-white shrink-0" />
              <span className="text-sm font-semibold text-white truncate">Location Catalog</span>
              <span className="text-xs text-white opacity-70 shrink-0">({items.length})</span>
            </div>
            <button
              type="button"
              onClick={() => {
                setEditingLocation(null);
                setAddingLocation(true);
              }}
              disabled={loading}
              className="inline-flex items-center gap-1.5 shrink-0 rounded-lg border border-white/40 bg-white/10 px-2.5 py-1 text-xs font-semibold tracking-wide text-white shadow-sm backdrop-blur-sm transition-all duration-150 hover:border-white/60 hover:bg-white/20 hover:shadow active:scale-[0.98] disabled:pointer-events-none disabled:opacity-40"
              title="Add location to catalog"
            >
              <PlusIcon className="h-3.5 w-3.5 opacity-95" aria-hidden />
              Add
            </button>
          </div>
        </div>
        <p className="px-3 pt-2.5 pb-1 text-[11px] leading-snug text-gray-500">
          This Location Catalog only contains suggestions for pickers. When using, it copies the value as
          string into new records - there is no link to historical records.
        </p>
        {loading ? (
          <LoadingChipsSkeleton />
        ) : items.length === 0 ? (
          <div className="rounded-b-xl">
            <EmptyState message="No locations in catalog yet." />
          </div>
        ) : (
          <div className="p-3 pt-2 flex flex-wrap gap-2 rounded-b-xl">
            {items.map((item) => {
              const menuOpen = openMenuId === item.id;
              const inactive = item.is_active === false;
              return (
                <div
                  key={item.id}
                  data-location-menu-root={item.id}
                  className={`inline-flex items-stretch gap-1 max-w-full rounded-xl border pl-3 py-2 pr-1 shadow-sm transition-colors ${
                    inactive
                      ? 'border-gray-200 bg-gray-100/80 opacity-90'
                      : 'border-gray-200 bg-gray-50/90 hover:border-gray-300'
                  }`}
                >
                  <div className="flex flex-col min-w-0 flex-1 justify-center py-0.5">
                    <span className="text-sm text-gray-800 font-medium truncate">
                      {item[labelKey]}
                    </span>
                    {subKey && item[subKey] ? (
                      <span className="text-xs text-gray-400 mt-0.5 truncate">{item[subKey]}</span>
                    ) : null}
                    {inactive ? (
                      <span className="text-[10px] uppercase tracking-wide text-amber-700 mt-0.5">
                        Inactive
                      </span>
                    ) : null}
                  </div>
                  <div className="relative flex items-start shrink-0">
                    <button
                      type="button"
                      className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-200/80 hover:text-gray-700 transition-colors disabled:opacity-40"
                      title="More actions"
                      aria-label="More actions"
                      aria-expanded={menuOpen}
                      onClick={() => setOpenMenuId(menuOpen ? null : item.id)}
                    >
                      <EllipsisVerticalIcon className="h-5 w-5" />
                    </button>
                    {menuOpen && (
                      <div
                        className="absolute z-40 right-0 top-full mt-0.5 w-max min-w-0 rounded-lg border border-gray-200 bg-white py-0.5 shadow-lg"
                        role="menu"
                      >
                        <button
                          type="button"
                          role="menuitem"
                          className="flex w-full items-center gap-2 whitespace-nowrap pl-2.5 pr-3 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-50"
                          onClick={() => {
                            setOpenMenuId(null);
                            setAddingLocation(false);
                            setEditingLocation(item);
                          }}
                        >
                          <PencilSquareIcon className="h-4 w-4 shrink-0" />
                          Edit
                        </button>
                        <button
                          type="button"
                          role="menuitem"
                          className="flex w-full items-center gap-2 whitespace-nowrap pl-2.5 pr-3 py-1.5 text-left text-sm text-red-600 hover:bg-red-50"
                          onClick={() => {
                            setOpenMenuId(null);
                            setDeleteTarget(item);
                          }}
                        >
                          <TrashIcon className="h-4 w-4 shrink-0" />
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <ModalEditAssetLocation
        isOpen={locationFormOpen}
        location={editingLocation}
        onClose={closeLocationForm}
        onSaved={reload}
      />

      <ModalConfirm
        isOpen={Boolean(deleteTarget)}
        onClose={() => !deleting && setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
        title="Remove location from catalog"
        message={
          deleteTarget
            ? `Remove "${deleteTarget.location_name}" from the location catalog? This does not change any existing assets or historical records—they keep their stored location text.`
            : ''
        }
        confirmText={deleting ? 'Removing…' : 'Remove'}
        cancelText="Cancel"
        confirmButtonClass={`px-4 py-2 rounded-xl ${deleting ? 'bg-gray-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'} text-white shadow`}
        cancelButtonClass={`px-4 py-2 rounded-xl border border-gray-300 ${deleting ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`}
        disabled={deleting}
      />
    </>
  );
};

/** Prominent line: prefer Level 3, then 2 → 1 → 0; if all empty, use category code only. */
const categoryChipPrimary = (item) => {
  const l3 = item.level_3?.trim();
  if (l3) return l3;
  const l2 = item.level_2?.trim();
  if (l2) return l2;
  const l1 = item.level_1?.trim();
  if (l1) return l1;
  const l0 = item.level_0?.trim();
  if (l0) return l0;
  return item.category_code?.trim() || '';
};

const categoryChipOrder = (item) => {
  const n = Number(item.order);
  if (Number.isFinite(n)) return n;
  const p = parseInt(String(item.order ?? ''), 10);
  return Number.isFinite(p) ? p : 0;
};

/** Group catalog rows by `level_1`; sort groups by minimum `order`, then label. */
const groupCategoriesByLevel1 = (items) => {
  const map = new Map();
  for (const item of items) {
    const key = item.level_1?.trim() ?? '';
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(item);
  }
  for (const arr of map.values()) {
    arr.sort((a, b) => {
      const d = categoryChipOrder(a) - categoryChipOrder(b);
      if (d !== 0) return d;
      return (a.category_code || '').localeCompare(b.category_code || '');
    });
  }
  const keys = [...map.keys()];
  const minOrder = (k) => Math.min(...map.get(k).map((it) => categoryChipOrder(it)));
  keys.sort((ka, kb) => {
    const oa = minOrder(ka);
    const ob = minOrder(kb);
    if (oa !== ob) return oa - ob;
    const la = ka || '\uffff';
    const lb = kb || '\uffff';
    return la.localeCompare(lb);
  });
  return keys.map((key) => ({
    key: key || '__uncat__',
    title: key || 'Uncategorized',
    items: map.get(key),
  }));
};

/** Categories: same chip / catalog pattern as locations. */
const CategoriesChipsCard = ({ items, loading, reload }) => {
  const [openMenuId, setOpenMenuId] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [addingCategory, setAddingCategory] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const closeCategoryForm = () => {
    setAddingCategory(false);
    setEditingCategory(null);
  };

  const categoryFormOpen = addingCategory || editingCategory != null;

  const groupedCategories = useMemo(() => groupCategoriesByLevel1(items), [items]);

  useEffect(() => {
    if (openMenuId == null) return;
    const handlePointerDown = (e) => {
      const root = e.target.closest('[data-category-menu-root]');
      if (root && root.getAttribute('data-category-menu-root') === String(openMenuId)) {
        return;
      }
      setOpenMenuId(null);
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [openMenuId]);

  const handleConfirmDeleteCategory = async () => {
    if (!deleteTarget?.id) return;
    try {
      setDeleting(true);
      await deleteAssetCategory(deleteTarget.id);
      toast.success('Category removed from catalog.');
      setDeleteTarget(null);
      if (reload) await reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data) || err.message || 'Failed to delete category.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <div className="bg-white shadow rounded-xl">
        <div className="rounded-t-xl overflow-hidden">
          <div className="bg-green-800 px-3 py-1.5 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <TagIcon className="h-4 w-4 text-white shrink-0" />
              <span className="text-sm font-semibold text-white truncate">Category Catalog</span>
              <span className="text-xs text-white opacity-70 shrink-0">({items.length})</span>
            </div>
            <button
              type="button"
              onClick={() => {
                setEditingCategory(null);
                setAddingCategory(true);
              }}
              disabled={loading}
              className="inline-flex items-center gap-1.5 shrink-0 rounded-lg border border-white/40 bg-white/10 px-2.5 py-1 text-xs font-semibold tracking-wide text-white shadow-sm backdrop-blur-sm transition-all duration-150 hover:border-white/60 hover:bg-white/20 hover:shadow active:scale-[0.98] disabled:pointer-events-none disabled:opacity-40"
              title="Add category to catalog"
            >
              <PlusIcon className="h-3.5 w-3.5 opacity-95" aria-hidden />
              Add
            </button>
          </div>
        </div>
        <p className="px-3 pt-2.5 pb-1 text-[11px] leading-snug text-gray-500">
          The Category Catalog suggests picker options and controls view aggregation. It stores the category
          code as a string in new records, with no link to historical data - only affecting the aggregated
          views.
        </p>
        {loading ? (
          <LoadingChipsSkeleton />
        ) : items.length === 0 ? (
          <div className="rounded-b-xl">
            <EmptyState message="No categories in catalog yet." />
          </div>
        ) : (
          <div className="p-3 pt-2 flex flex-col gap-0 rounded-b-xl">
            {groupedCategories.map((group, gIdx) => (
              <div key={group.key} className={`w-full ${gIdx > 0 ? 'mt-4' : ''}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-xs font-medium text-gray-700 shrink-0 max-w-[45%] truncate">
                    {group.title}
                  </span>
                  <span className="h-px flex-1 bg-gray-200 min-w-[0.5rem]" aria-hidden />
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {group.items.map((item) => {
                    const menuOpen = openMenuId === item.id;
                    const primary = categoryChipPrimary(item);
                    const code = item.category_code?.trim() || '';
                    const hasHierarchyLabel = Boolean(
                      item.level_3?.trim() ||
                        item.level_2?.trim() ||
                        item.level_1?.trim() ||
                        item.level_0?.trim()
                    );
                    const showCodeSecondary = hasHierarchyLabel && code;
                    const ord = categoryChipOrder(item);
                    return (
                      <div
                        key={item.id}
                        data-category-menu-root={item.id}
                        className="inline-flex items-stretch gap-1 max-w-full rounded-xl border border-gray-200 bg-gray-50/90 pl-3 py-2 pr-1 shadow-sm transition-colors hover:border-gray-300"
                      >
                        <div className="flex flex-col min-w-0 flex-1 justify-center py-0.5">
                          <span className="text-sm text-gray-800 font-medium flex items-baseline gap-1 min-w-0">
                            <span className="tabular-nums text-gray-500 shrink-0">{ord}.</span>
                            <span className="truncate">{primary}</span>
                          </span>
                          {showCodeSecondary ? (
                            <span className="text-[11px] text-gray-400 mt-0.5 truncate">{code}</span>
                          ) : null}
                        </div>
                        <div className="relative flex items-start shrink-0">
                          <button
                            type="button"
                            className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-200/80 hover:text-gray-700 transition-colors"
                            title="More actions"
                            aria-label="More actions"
                            aria-expanded={menuOpen}
                            onClick={() => setOpenMenuId(menuOpen ? null : item.id)}
                          >
                            <EllipsisVerticalIcon className="h-5 w-5" />
                          </button>
                          {menuOpen && (
                            <div
                              className="absolute z-40 right-0 top-full mt-0.5 w-max min-w-0 rounded-lg border border-gray-200 bg-white py-0.5 shadow-lg"
                              role="menu"
                            >
                              <button
                                type="button"
                                role="menuitem"
                                className="flex w-full items-center gap-2 whitespace-nowrap pl-2.5 pr-3 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-50"
                                onClick={() => {
                                  setOpenMenuId(null);
                                  setAddingCategory(false);
                                  setEditingCategory(item);
                                }}
                              >
                                <PencilSquareIcon className="h-4 w-4 shrink-0" />
                                Edit
                              </button>
                              <button
                                type="button"
                                role="menuitem"
                                className="flex w-full items-center gap-2 whitespace-nowrap pl-2.5 pr-3 py-1.5 text-left text-sm text-red-600 hover:bg-red-50"
                                onClick={() => {
                                  setOpenMenuId(null);
                                  setDeleteTarget(item);
                                }}
                              >
                                <TrashIcon className="h-4 w-4 shrink-0" />
                                Delete
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ModalEditAssetCategory
        isOpen={categoryFormOpen}
        category={editingCategory}
        onClose={closeCategoryForm}
        onSaved={reload}
      />

      <ModalConfirm
        isOpen={Boolean(deleteTarget)}
        onClose={() => !deleting && setDeleteTarget(null)}
        onConfirm={handleConfirmDeleteCategory}
        title="Remove category from catalog"
        message={
          deleteTarget
            ? `Remove "${deleteTarget.category_code}" from the category catalog? This does not change any existing assets or historical records—they keep their stored category code.`
            : ''
        }
        confirmText={deleting ? 'Removing…' : 'Remove'}
        cancelText="Cancel"
        confirmButtonClass={`px-4 py-2 rounded-xl ${deleting ? 'bg-gray-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'} text-white shadow`}
        cancelButtonClass={`px-4 py-2 rounded-xl border border-gray-300 ${deleting ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`}
        disabled={deleting}
      />
    </>
  );
};

// ###### MAIN COMPONENT
const AssetsManage = ({ yearsRange }) => {
  const { locations, categories, assets, loading: loadingRefs, error, refreshFull } = useAssetsData();

  return (
    <div className="flex flex-col gap-2 w-full mt-2">
      <CardAssetsImport reload={refreshFull} />
      <CardCustomUpdateBalances />
      <CardAssetsUpdateData reload={refreshFull} />
      <CardYearReport yearsRange={yearsRange} />

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
        <LocationsChipsCard
          items={locations}
          labelKey="location_name"
          subKey="type"
          loading={loadingRefs}
          reload={refreshFull}
        />
        <CategoriesChipsCard items={categories} loading={loadingRefs} reload={refreshFull} />
      </div>

      <FabAssetsMenu assets={assets} onSuccess={refreshFull} />
    </div>
  );
};

export default AssetsManage;
